import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const fetcher = (url) => api.get(url).then((r) => r.data);
const CHART_COLORS = ["#22C55E", "#3B82F6", "#F59E0B", "#EF4444", "#A855F7", "#EC4899", "#14B8A6"];

const formatNumberWithCommas = (value) => {
  if (!value) return "";
  const clean = String(value).replace(/\D/g, "");
  return clean.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const parseNumberFromCommas = (value) => {
  if (!value) return 0;
  return Number(String(value).replace(/,/g, ""));
};

const getFontSizeClass = (value, baseSize = "text-3xl") => {
  const len = value ? String(value).length : 0;
  if (baseSize === "text-3xl") {
    if (len > 16) return "text-base";
    if (len > 14) return "text-lg";
    if (len > 11) return "text-xl";
    if (len > 9) return "text-2xl";
    return "text-3xl";
  }
  if (len > 16) return "text-sm";
  if (len > 14) return "text-base";
  if (len > 11) return "text-lg";
  if (len > 9) return "text-xl";
  return "text-2xl";
};

const getTableFontSizeClass = (value) => {
  const len = value ? String(value).length : 0;
  if (len > 15) return "text-[10px]";
  if (len > 12) return "text-xs";
  return "text-sm";
};

export default function Portfolio() {
  const { data: holdings } = useSWR("/holdings", fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ symbol: "", name: "", shares: "", avg_price: "", current_price: "", asset_type: "stock_lot" });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const create = async () => {
    if (!form.symbol.trim() || !form.name.trim()) return toast.error("Simbol dan nama wajib");
    try {
      await api.post("/holdings", {
        ...form,
        shares: Number(form.shares),
        avg_price: parseNumberFromCommas(form.avg_price),
        current_price: parseNumberFromCommas(form.current_price),
      });
      toast.success("Holding ditambahkan");
      mutate("/holdings"); mutate("/reports/summary");
      setOpen(false); setForm({ symbol: "", name: "", shares: "", avg_price: "", current_price: "", asset_type: "stock_lot" });
    } catch { toast.error("Gagal"); }
  };
  const del = async (id) => {
    try { await api.delete(`/holdings/${id}`); mutate("/holdings"); mutate("/reports/summary"); } catch {}
  };

  const handleConfirmDelete = () => {
    if (confirmId) {
      del(confirmId);
      setConfirmId(null);
    }
    setConfirmOpen(false);
  };

  const getHoldingValue = (h) => {
    const factor = h.asset_type === "stock_lot" ? 100 : 1;
    return h.shares * factor * h.current_price;
  };
  const getHoldingCost = (h) => {
    const factor = h.asset_type === "stock_lot" ? 100 : 1;
    return h.shares * factor * h.avg_price;
  };

  const totalValue = (holdings || []).reduce((s, h) => s + getHoldingValue(h), 0);
  const totalCost = (holdings || []).reduce((s, h) => s + getHoldingCost(h), 0);
  const gainLoss = totalValue - totalCost;
  const gainPct = totalCost ? (gainLoss / totalCost) * 100 : 0;

  const chartData = (holdings || []).map((h) => ({ name: h.symbol, value: getHoldingValue(h) }));

  return (
    <div className="animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4">
        <div>
          <p className="label-caps">Investment</p>
          <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mt-1">Investment Portfolio</h1>
          <p className="text-sm text-muted-foreground mt-2">Pantau saham, kripto, reksa dana, dan obligasi.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-foreground text-background hover:bg-foreground/90 h-11" data-testid="add-holding-btn">
              <Plus className="h-4 w-4 mr-2" /> Tambah Aset
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Aset Baru</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2">
              <div><Label>Simbol</Label><Input value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })} className="mt-2 h-11 font-mono" placeholder="BBCA" data-testid="holding-symbol-input" /></div>
              <div><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-11" placeholder="Bank BCA" data-testid="holding-name-input" /></div>
              <div><Label>Jumlah Lot/Unit</Label><Input type="number" value={form.shares} onChange={(e) => setForm({ ...form, shares: e.target.value })} className="mt-2 h-11 font-mono" data-testid="holding-shares-input" /></div>
              <div>
                <Label>Tipe</Label>
                <Select value={form.asset_type} onValueChange={(v) => setForm({ ...form, asset_type: v })}>
                  <SelectTrigger className="mt-2 h-11" data-testid="holding-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stock_lot">Saham (Lot)</SelectItem>
                    <SelectItem value="stock">Saham (Lembar)</SelectItem>
                    <SelectItem value="crypto">Kripto</SelectItem>
                    <SelectItem value="mutual_fund">Reksa Dana</SelectItem>
                    <SelectItem value="bond">Obligasi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Harga Rata2</Label><Input type="text" value={form.avg_price} onChange={(e) => setForm({ ...form, avg_price: formatNumberWithCommas(e.target.value) })} className="mt-2 h-11 font-mono" placeholder="0" data-testid="holding-avg-input" /></div>
              <div><Label>Harga Saat Ini</Label><Input type="text" value={form.current_price} onChange={(e) => setForm({ ...form, current_price: formatNumberWithCommas(e.target.value) })} className="mt-2 h-11 font-mono" placeholder="0" data-testid="holding-current-input" /></div>
            </div>
            <DialogFooter><Button onClick={create} className="bg-foreground text-background" data-testid="create-holding-submit">Simpan</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
        <div className="border border-border bg-card p-5">
          <p className="label-caps">Total Nilai</p>
          <p className={`metric-num mt-3 whitespace-nowrap truncate ${getFontSizeClass(formatIDR(totalValue), "text-3xl")}`}>{formatIDR(totalValue)}</p>
        </div>
        <div className="border border-border bg-card p-5">
          <p className="label-caps">Total Modal</p>
          <p className={`metric-num mt-3 whitespace-nowrap truncate ${getFontSizeClass(formatIDR(totalCost), "text-3xl")}`}>{formatIDR(totalCost)}</p>
        </div>
        <div className="border border-border bg-card p-5">
          <p className="label-caps">Unrealized P&L</p>
          <p className={`metric-num mt-3 whitespace-nowrap truncate ${getFontSizeClass((gainLoss >= 0 ? "+" : "") + formatIDR(gainLoss), "text-3xl")} ${gainLoss >= 0 ? "text-gain" : "text-loss"}`}>{gainLoss >= 0 ? "+" : ""}{formatIDR(gainLoss)}</p>
          <p className={`text-xs mt-1 ${gainLoss >= 0 ? "text-gain" : "text-loss"}`}>{gainPct.toFixed(2)}%</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-1 border border-border bg-card p-5">
          <p className="label-caps mb-4">Alokasi</p>
          {chartData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">Belum ada data</div>
          ) : (
            <div className="h-64" data-testid="portfolio-allocation-chart">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={chartData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                    {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 border border-border bg-card overflow-hidden">
          {/* Table header (only on desktop) */}
          <div className="hidden md:grid grid-cols-12 label-caps px-4 py-3 border-b border-border bg-secondary/40">
            <div className="col-span-1">Simbol</div>
            <div className="col-span-3">Nama</div>
            <div className="col-span-1 text-right">Unit</div>
            <div className="col-span-3 text-right">Harga</div>
            <div className="col-span-3 text-right">Nilai</div>
            <div className="col-span-1"></div>
          </div>
          {(holdings || []).length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Belum ada aset.</div>
          ) : (holdings || []).map((h) => {
            const value = getHoldingValue(h);
            const pnl = value - getHoldingCost(h);
            return (
              <div key={h.id} className="flex flex-col md:grid md:grid-cols-12 px-4 py-4 md:py-3 border-b border-border last:border-0 items-start md:items-center hover:bg-accent/40 gap-2 md:gap-0" data-testid={`holding-row-${h.id}`}>
                <div className="col-span-1 font-mono font-medium truncate flex justify-between w-full md:w-auto">
                  <span className="text-sm font-semibold">{h.symbol}</span>
                  <span className="inline md:hidden text-xs text-muted-foreground uppercase bg-secondary/40 px-1 py-0.5 rounded font-sans">
                    {h.asset_type === "stock_lot" ? "stock (lot)" : h.asset_type === "stock" ? "stock" : h.asset_type}
                  </span>
                </div>
                <div className="col-span-3 text-sm truncate pr-2 w-full md:w-auto hidden md:block">
                  <div className="font-medium truncate">{h.name}</div>
                  <div className="text-xs text-muted-foreground uppercase">
                    {h.asset_type === "stock_lot" ? "stock (lot)" : h.asset_type === "stock" ? "stock" : h.asset_type}
                  </div>
                </div>
                <div className="col-span-3 text-sm truncate pr-2 w-full md:w-auto block md:hidden">
                  <div className="font-medium truncate text-muted-foreground">{h.name}</div>
                </div>
                <div className="col-span-1 md:text-right font-mono text-sm whitespace-nowrap w-full md:w-auto flex md:block justify-between">
                  <span className="inline md:hidden text-xs text-muted-foreground/60 mr-1.5 uppercase font-medium">Unit:</span>
                  <span>{Number(h.shares)} {h.asset_type === "stock_lot" ? "Lot" : ""}</span>
                </div>
                <div className={`col-span-3 md:text-right font-mono whitespace-nowrap w-full md:w-auto flex md:block justify-between ${getTableFontSizeClass(formatIDR(h.current_price))}`}>
                  <span className="inline md:hidden text-xs text-muted-foreground/60 mr-1.5 uppercase font-medium">Harga:</span>
                  <span>{formatIDR(h.current_price)}</span>
                </div>
                <div className="col-span-3 md:text-right w-full md:w-auto flex md:block justify-between items-baseline">
                  <span className="inline md:hidden text-xs text-muted-foreground/60 mr-1.5 uppercase font-medium">Nilai / P&L:</span>
                  <div className="text-right flex flex-col md:block">
                    <span className={`font-mono whitespace-nowrap ${getTableFontSizeClass(formatIDR(value))}`}>{formatIDR(value)}</span>
                    <span className={`text-xs font-mono whitespace-nowrap ml-1.5 md:ml-0 md:block ${pnl >= 0 ? "text-gain" : "text-loss"}`}>
                      ({pnl >= 0 ? "+" : ""}{formatIDR(pnl)})
                    </span>
                  </div>
                </div>
                <div className="col-span-1 text-right w-full md:w-auto border-t border-border/40 pt-2 md:pt-0 md:border-0 flex justify-end">
                  <Button variant="ghost" size="icon" onClick={() => { setConfirmId(h.id); setConfirmOpen(true); }} data-testid={`holding-delete-${h.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Konfirmasi Hapus</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-sm text-muted-foreground">
            Apakah Anda yakin ingin menghapus aset investasi ini?
          </div>
          <DialogFooter className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Batal</Button>
            <Button variant="destructive" className="bg-loss text-white hover:bg-loss/90" onClick={handleConfirmDelete}>Hapus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
